/**
 * Development Environment Initializer
 * 
 * Automatically initializes development-specific configurations at app startup,
 * including fetching a default shift plan nodeId for middleware injection.
 */

import { isLocalEnvironment } from 'src/utils/environmentDetection';
import { MockDataService } from 'src/devDataProviders/mockDataService';
import { store } from 'src/store';
import { setStationCode } from 'src/store/slices/shiftPlanSlice';
import { setDarkModeFromParent } from 'src/store/slices/themeSlice';

/**
 * Global development configuration
 */
interface DevConfig {
  defaultNodeId?: string;
  defaultWarehouseId?: string;
  isInitialized: boolean;
}

let devConfig: DevConfig = {
  isInitialized: false
};

/**
 * Get the current development configuration
 */
export function getDevConfig(): DevConfig {
  return devConfig;
}

/**
 * Get the default nodeId for API requests
 */
export function getDefaultNodeId(): string | undefined {
  return devConfig.defaultNodeId;
}

/**
 * Get the default warehouseId for API requests  
 */
export function getDefaultWarehouseId(): string | undefined {
  return devConfig.defaultWarehouseId;
}

/**
 * Initialize development environment configurations
 * 
 * This function runs at app startup in development environment to:
 * 1. Immediately set fallback stationCode to prevent warehouse modal
 * 2. Fetch a default shift plan from mock service
 * 3. Extract and store the nodeId for middleware use
 * 4. Set up any other development-specific configurations
 */
export async function initializeDevEnvironment(): Promise<void> {
  // Only run in local development environment
  if (!isLocalEnvironment()) {
    console.log('[Dev Initializer] Skipping development initialization - not in local environment');
    return;
  }

  if (devConfig.isInitialized) {
    console.log('[Dev Initializer] Already initialized');
    return;
  }

  console.log('[Dev Initializer] 🚀 Starting development environment initialization...');

  // Set up mock parent message responder for postMessage-based functions
  // In production, the parent window responds to these. Locally, window.parent === window
  // and nobody responds, causing timeouts. This listener simulates parent responses.
  setupMockParentMessageResponder();


  // IMMEDIATELY set fallback configuration to prevent warehouse modal
  const fallbackStationCode = 'LAS2';
  devConfig = {
    defaultNodeId: fallbackStationCode,
    defaultWarehouseId: fallbackStationCode,
    isInitialized: true
  };

  // Dispatch fallback to Redux store immediately
  store.dispatch(setStationCode({ stationCode: fallbackStationCode }));
  console.log('[Dev Initializer] ⚡ Immediate fallback configuration set: LAS2');

  try {
    // Fetch a default shift plan to get nodeId (this happens asynchronously)
    const defaultShiftPlan = await MockDataService.getShiftPlan();
    
    // Extract nodeId and warehouseId from shift plan with type checking
    const nodeId = typeof defaultShiftPlan.nodeId === 'string' ? defaultShiftPlan.nodeId : fallbackStationCode;
    const warehouseId = nodeId; // In this system, nodeId often equals warehouseId
    
    // Update development configuration with actual values
    devConfig = {
      defaultNodeId: nodeId,
      defaultWarehouseId: warehouseId,
      isInitialized: true
    };

    // Update Redux store with actual stationCode if different from fallback
    if (nodeId !== fallbackStationCode) {
      store.dispatch(setStationCode({ stationCode: nodeId }));
    }

    // Initialize dark mode from mock service
    try {
      const darkModeResponse = await MockDataService.getDarkModePreference();
      const isDark = darkModeResponse.args.darkMode === 'dark';
      store.dispatch(setDarkModeFromParent(isDark ? 'DARK' : 'LIGHT'));
      console.log(`[Dev Initializer] 🌙 Dark mode set to: ${isDark ? 'DARK' : 'LIGHT'} (via MockDataService)`);
    } catch (darkModeError) {
      console.warn('[Dev Initializer] Could not get dark mode preference, defaulting to LIGHT');
      store.dispatch(setDarkModeFromParent('LIGHT'));
    }

    console.log('[Dev Initializer] ✅ Development environment initialized:');
    console.log(`  • Default Node ID: ${nodeId}`);
    console.log(`  • Default Warehouse ID: ${warehouseId}`);
    console.log(`  • Mock data service ready with auto-injected parameters`);
    console.log(`  • Configuration dispatched to Redux store`);

  } catch (error) {
    console.error('[Dev Initializer] ❌ Failed to fetch from mock service, using fallback:', error);
    console.log('[Dev Initializer] 🔄 Continuing with fallback configuration: LAS2');
  }
}

/**
 * Reset development configuration (for testing)
 */
export function resetDevConfig(): void {
  devConfig = {
    isInitialized: false
  };
}

/**
 * Manually set development configuration (for testing)
 */
export function setDevConfig(config: Partial<DevConfig>): void {
  devConfig = { ...devConfig, ...config };
}

/**
 * Check if development environment has been initialized
 */
export function isDevInitialized(): boolean {
  return devConfig.isInitialized;
}

/**
 * Get comprehensive development status for debugging
 */
export function getDevStatus() {
  return {
    isLocalEnvironment: isLocalEnvironment(),
    isInitialized: devConfig.isInitialized,
    defaultNodeId: devConfig.defaultNodeId,
    defaultWarehouseId: devConfig.defaultWarehouseId,
    config: devConfig
  };
}

/**
 * Mock parent message responder for local development
 * 
 * In production, the parent window (ShiftPlanManagementWebsite) responds to postMessage
 * requests like getTcpEmployeeLogin, getEmployeeName, getTcpRoles, getDarkModeStatus.
 * Locally, window.parent === window and nobody responds, causing timeouts.
 * 
 * This listener intercepts outgoing postMessage calls and immediately responds
 * with mock data, simulating the parent window behavior.
 */
function setupMockParentMessageResponder(): void {
  const mockUserLogin = 'local-dev-user';
  const mockUserName = 'Local Dev User';

  window.addEventListener('message', (event: MessageEvent) => {
    const data = event.data;
    if (!data || !data.method || !data.guid) return;

    // Only handle methods that the parent would respond to
    switch (data.method) {
      case 'getTcpEmployeeLogin':
        window.dispatchEvent(new MessageEvent('message', {
          data: {
            method: 'sendTcpEmployeeLoginResponse',
            guid: data.guid,
            childData: { name: JSON.stringify(mockUserLogin) },
          },
        }));
        break;

      case 'getEmployeeName':
        window.dispatchEvent(new MessageEvent('message', {
          data: {
            method: 'sendEmployeeNameResponse',
            guid: data.guid,
            childData: { name: JSON.stringify(mockUserName) },
          },
        }));
        break;

      case 'getTcpRoles':
        window.dispatchEvent(new MessageEvent('message', {
          data: {
            method: 'sendTcpRolesResponse',
            guid: data.guid,
            childData: ['admin', 'user'],
          },
        }));
        break;

      case 'getDarkModeStatus':
        window.dispatchEvent(new MessageEvent('message', {
          data: {
            method: 'sendDarkModeStatus',
            guid: data.guid,
            childData: { capability: 'LIGHT' },
          },
        }));
        break;

      default:
        // Don't handle other methods (e.g., postGenericFromParent goes through apiMiddleware)
        break;
    }
  });

  console.log('[Dev Initializer] 📨 Mock parent message responder set up for local development');
}
